/**
 * Login and first-admin setup.
 *
 * Functional spec: `qt_poc/auth_qt.py` — same headings, same wording, same
 * password rules. The desktop shows a username *dropdown*; the web version
 * uses a text field on purpose: enumerating accounts to an unauthenticated
 * visitor is fine on a single-user desktop and not fine on a network service.
 */
import { useState } from 'react'
import {
  Alert, Anchor, Button, Card, Center, Group, PasswordInput, Stack, Text,
  TextInput, Title,
} from '@mantine/core'

import { useAuth } from '../auth/AuthContext'
import { ApiError } from '../api/client'
import { MIN_PASSWORD_LENGTH, passwordProblem } from '../auth/password'
import { ThemeToggle } from '../components/ThemeToggle'

function Shell({ title, subtitle, children }: {
  title: string
  subtitle: string
  children: React.ReactNode
}) {
  return (
    <Center mih="100dvh" p="md">
      {/* `maw` + `w="100%"`, not a bare `w={420}`: the login screen is the one
          route that renders before anything else in this pass has a chance to
          apply (no Shell, no AppShell padding) — a fixed 420px card on a
          390px phone overflowed `#root`'s `overflow-x: hidden` (index.css)
          and simply clipped, rather than scrolling into view, which is worse:
          part of the form was there but unreachable. */}
      <Card withBorder shadow="sm" padding="xl" radius="md" w="100%" maw={420}>
        <Group justify="space-between" mb="xs">
          <Title order={3}>{title}</Title>
          <ThemeToggle />
        </Group>
        <Text c="dimmed" size="sm" mb="lg">{subtitle}</Text>
        {children}
      </Card>
    </Center>
  )
}

export function LoginPage() {
  const { login, needsFirstAdmin } = useAuth()
  const [username, setUsername] = useState('')
  const [password, setPassword] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)

  if (needsFirstAdmin) return <FirstAdminPage />

  async function submit(event: React.FormEvent) {
    event.preventDefault()
    setError(null)
    if (!username.trim()) return setError('יש להזין שם משתמש.')
    if (!password) return setError('נא למלא סיסמה.')

    setBusy(true)
    try {
      await login(username.trim(), password)
    } catch (err) {
      // The server's message is deliberately identical for "no such user" and
      // "wrong password" — pass it through rather than inventing a more
      // specific one, which would re-open the account-enumeration hole.
      setError(err instanceof ApiError ? err.message : 'ההתחברות נכשלה.')
      setPassword('')
    } finally {
      setBusy(false)
    }
  }

  return (
    <Shell title="התחברות" subtitle="הקלד שם משתמש וסיסמה">
      <form onSubmit={submit}>
        <Stack>
          {error && <Alert color="red" variant="light">{error}</Alert>}
          <TextInput
            label="שם משתמש"
            value={username}
            onChange={(e) => setUsername(e.currentTarget.value)}
            autoFocus
            autoComplete="username"
            data-autofocus
          />
          <PasswordInput
            label="סיסמה"
            value={password}
            onChange={(e) => setPassword(e.currentTarget.value)}
            autoComplete="current-password"
          />
          <Button type="submit" loading={busy} fullWidth>התחבר</Button>
        </Stack>
      </form>
    </Shell>
  )
}

export function FirstAdminPage() {
  const { createFirstAdmin } = useAuth()
  const [username, setUsername] = useState('')
  const [password, setPassword] = useState('')
  const [confirm, setConfirm] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)

  const problem = password ? passwordProblem(password) : null

  async function submit(event: React.FormEvent) {
    event.preventDefault()
    setError(null)
    if (!username.trim() || !password) return setError('נא למלא שם משתמש וסיסמה.')
    if (password !== confirm) return setError('הסיסמאות אינן תואמות.')
    const weak = passwordProblem(password)
    if (weak) return setError(weak)

    setBusy(true)
    try {
      await createFirstAdmin(username.trim(), password)
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'יצירת המנהל נכשלה.')
    } finally {
      setBusy(false)
    }
  }

  return (
    <Shell
      title="יצירת מנהל ראשון"
      subtitle="ברוך הבא — צור את חשבון המנהל הראשון לפני השימוש."
    >
      <form onSubmit={submit}>
        <Stack>
          {error && <Alert color="red" variant="light">{error}</Alert>}
          <TextInput
            label="שם משתמש"
            value={username}
            onChange={(e) => setUsername(e.currentTarget.value)}
            autoFocus
            autoComplete="username"
          />
          <PasswordInput
            label="סיסמה"
            value={password}
            onChange={(e) => setPassword(e.currentTarget.value)}
            error={problem}
            autoComplete="new-password"
          />
          <PasswordInput
            label="אימות סיסמה"
            value={confirm}
            onChange={(e) => setConfirm(e.currentTarget.value)}
            error={confirm && confirm !== password ? 'הסיסמאות אינן תואמות.' : null}
            autoComplete="new-password"
          />
          <Text size="xs" c="dimmed">
            לפחות {MIN_PASSWORD_LENGTH} תווים, וכולל לפחות ספרה או סימן אחד.
          </Text>
          <Button type="submit" loading={busy} fullWidth>צור מנהל</Button>
          <Anchor size="xs" c="dimmed" component="button" type="button" onClick={() => window.location.reload()}>
            כבר קיים חשבון? רענן את העמוד
          </Anchor>
        </Stack>
      </form>
    </Shell>
  )
}
